import _ from 'lodash';
import { useState, useMemo, useEffect } from 'react';

import { login, getLoginMessageConfig } from 'sso/api';
import { useFieldsetLite } from 'controls/hooks/useFieldsetLite';
import { InfoMessage, Button } from 'sso/components/common';
import { LOCALIZATION_TEXTS } from 'sso/constants/localization';
import { getLanguageStrings } from 'sso/constants/localization/langUtils';
import { Buffer } from 'buffer';
import visibility from 'assets/img/visibility.svg';
import visibilityOff from 'assets/img/visibility-off.svg';
import capslock from 'assets/img/keyboard_capslock_black.svg';
import { Success } from './success';

let loginMessageConfigStore = {};
const loginFormTemplate = { email: '', password: '' };

const isValidURL = (string) => {
  try {
    return Boolean(new URL(string));
  } catch (err) {
    return false;
  }
};

const SING_UP_PARAMETER_FLAG = 'is_sign_up';
const isSingUp = (queryParams) => _.get(queryParams, SING_UP_PARAMETER_FLAG) === 'true';

const submitForm = async (
  {
    setError, decodedData, history, strings,
  },
  formBody,
) => {
  const {
    accountNotActivated,
    here,
    resend,
    incorrectCredentials,
    unknownError,
  } = strings;

  try {
    const {
      success, action, redirectTo, error,
    } = await login(
      formBody,
      decodedData,
    );
    const signUpUrl = `${
      process.env.REACT_APP_RCW_ACCOUNT_URL
    }/signup?redirect_uri=${encodeURIComponent(
      window.location.href,
    )}&email=${encodeURIComponent(formBody.email)}`;

    if (success) {
      switch (action) {
        case 'redirect': {
          window.location.replace(redirectTo);
          await new Promise((res) => setTimeout(res, 20000));
          break;
        }
        case 'migrate':
          history.replace('/migrate', { signUpUrl });
          break;
        default:
          throw new Error({ action, error: 'unknown action' });
      }
    } else {
      const errorLink = _.includes(error, 'UserNotConfirmedException') ? (
        <>
          {accountNotActivated}
          {' '}
          <a href={signUpUrl} className='b underline white'>
            {here}
          </a>
          {' '}
          {resend}
        </>
      ) : (
        <>
          {incorrectCredentials}
          {' '}
          <a href={signUpUrl} className='b underline white'>
            {here}
          </a>
          .
        </>
      );
      setError(errorLink);
    }
  } catch (err) {
    console.error(err);
    setError(unknownError);
  }
  return false;
};

export const Login = ({ history }) => {
  const [loginMessageConfig, setLoginMessageConfig] = useState(
    loginMessageConfigStore,
  );
  const [error, setError] = useState();
  const [keyEvents, setKeyEvents] = useState({ capsLock: false, passVisibility: false, focusVisibility: { email: false, password: false } });
  const [isCaps, setIsCaps] = useState(false);

  const hasPlainRedirectUri = useMemo(
    () => new URLSearchParams(window.location.search).has('redirect_uri'),
    [],
  );
  const [isSuccessLanding, setIsSuccessLanding] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [locStrings, setLocStrings] = useState(LOCALIZATION_TEXTS.us);
  const [willRedirectToSignUp, setWillRedirectToSignUp] = useState(null);
  const [queryParams, setQueryParams] = useState(() => {
    let queryString = window.location.search;
    const splitParams = queryString.split('?');

    if (!hasPlainRedirectUri) {
      try {
        queryString = atob(splitParams[1]);
      } catch (err) {
        // do nothing
      }
    }
    const decodedData = Object.fromEntries(new URLSearchParams(queryString));
    // check if query comes with lang params
    const lang = 'lang' in decodedData ? decodedData.lang.toLowerCase() : 'us';
    return { decodedData, lang };
  });

  const { decodedData } = queryParams;

  const signUpUrl = `${
    process.env.REACT_APP_RCW_ACCOUNT_URL
  }/signup?redirect_uri=${encodeURIComponent(window.location.href)}`;
  const forgotUrl = process.env.REACT_APP_FORGOT_LINK;
  const hasValidParams = isValidURL(_.get(decodedData, 'redirect_uri'));
  const isSignUpCall = isSingUp(decodedData);

  if (willRedirectToSignUp) {
    const locationHref = `${window.location.origin}${
      window.location.pathname
    }?${Buffer.from(new URLSearchParams(decodedData).toString()).toString('base64')}`;
    window.location.href = `${
      process.env.REACT_APP_RCW_ACCOUNT_URL
    }/signup?redirect_uri=${encodeURIComponent(locationHref)}`;
  }

  useEffect(() => {
    if (!_.isEmpty(loginMessageConfigStore)) {
      return;
    }

    getLoginMessageConfig()
      .then((config) => (loginMessageConfigStore = config))
      .then(setLoginMessageConfig);
  }, []);

  useEffect(() => {
    // successfulLogin Landing params
    const params = window.location.search.split('?');

    if (params[1] === 'successfulLogin') {
      setIsSuccessLanding(true);

      // get lang from query (if any)
      if (params.length > 2 && params[2]) {
        setQueryParams((oldParams) => ({
          ...oldParams,
          lang:
            params[2].split('=').length > 1
              ? params[2].split('=')[1].toLowerCase()
              : 'us',
        }));
      }
    }
    // avoid invalid request flash
    setIsMounted(true);

    // set lang loc strings
    const langStrings = getLanguageStrings(queryParams?.lang);
    setLocStrings(langStrings);

    if (hasPlainRedirectUri) {
      window.history.replaceState(
        null,
        null,
        `${window.location.pathname}?${Buffer.from(window.location.search).toString('base64')}`,
      );
    } // lets encode it

    // if params contain a is_sign_up flag === true,
    // set the state for redirect and update params
    if (hasValidParams && isSignUpCall) {
      setWillRedirectToSignUp(true);
      setQueryParams((oldQueryParams) => ({ ...oldQueryParams, decodedData: { ...oldQueryParams.decodedData, is_sign_up: 'false' } }));
    }
  }, [hasPlainRedirectUri, queryParams.lang, hasValidParams, isSignUpCall]);

  const handleCapsLockOnOff = (e) => {
    if (e.getModifierState('CapsLock')) {
      setIsCaps(true);
    } else {
      setIsCaps(false);
    }
  };

  /**
   *
   * @param {string} eType onBlur|onFocus|onClick
   * @param {string} input email|password|visibility
   */
  const handleSetKeyEvent = (eType, input) => {
    switch (eType) {
      case 'onBlur':
        if (input === 'password') {
          setKeyEvents((oldKeyEvents) => ({ ...oldKeyEvents, capsLock: false }));
        }
        break;
      case 'onFocus':
        if (input === 'email') {
          setKeyEvents((oldKeyEvents) => ({ ...oldKeyEvents, focusVisibility: { email: true, password: false } }));
        }
        if (input === 'password') {
          setKeyEvents((oldKeyEvents) => ({ ...oldKeyEvents, focusVisibility: { email: false, password: true } }));
        }
        break;
      case 'onClick':
        if (input === 'visibility') {
          setKeyEvents((oldKeyEvents) => ({ ...oldKeyEvents, passVisibility: !oldKeyEvents.passVisibility }));
        }
        break;
      default:
        break;
    }
  };

  const {
    signIn,
    invalidRequest,
    email: labelEmail,
    password: labelPassword,
    newToRoland,
    joinNow,
    forgotPw,
    resetHere,
    launchingRCM,
    success,
    toContinue,
    youreSignedIn,
  } = locStrings;

  const {
    fields: { email, password },
    submit,
    loading,
  } = useFieldsetLite({
    schema: locStrings.validation.schemas.loginSchema,
    source: loginFormTemplate,
    onSubmit: submitForm.bind(null, {
      setError,
      decodedData,
      history,
      strings: locStrings,
    }),
  });

  if (isSuccessLanding) {
    return (
      <Success
        message={launchingRCM}
        info={{ success, toContinue, youreSignedIn }}
      />
    );
  }

  if ((hasValidParams && isSignUpCall) || willRedirectToSignUp) {
    return (
      <div className='min-vh-100 flex flex-column'>
        <div
          className='mh4 mw5 mv3 flex-auto self-center flex flex-column items-center justify-center'
          style={{ maxHeight: '150px' }}
        >
          <img
            className='mw5 mt4'
            src={require('assets/img/logo-no-bg.png')}
            style={{ objectFit: 'contain', maxHeight: '1.2rem' }}
            alt='logo'
          />
          <InfoMessage message='Please wait...' />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className='login center mw6'>
      <div className='min-vh-100 flex flex-column'>
        <div
          className='mh4 mw5 mt5 flex-auto self-center flex flex-column items-center justify-center'
          style={{ maxHeight: '125px' }}
        >
          <img
            className='mw5 mt2'
            src={require('assets/img/logo-no-bg.png')}
            style={{ objectFit: 'contain', maxHeight: '1.65rem' }}
            alt='logo'
          />
        </div>
        <div className='flex flex-column center'>
          <span className='white login__header'>{signIn}</span>
        </div>
        <div className='flex flex-column'>
          {error && <div className='br2 bg-red pa3 white mv3 tc'>{error}</div>}
          {!hasValidParams && isMounted && (
            <div className='br2 bg-red pa3 white mv3 tc'>{invalidRequest}</div>
          )}
          {hasValidParams && (
            <>
              {!error
                && loginMessageConfig.enabled
                && loginMessageConfig.message && (
                <InfoMessage message={loginMessageConfig.message} />
              )}
              <label className='white mv2'>{labelEmail}</label>
              <div className={`email__wrapper ${keyEvents.focusVisibility?.email ? 'isFocused' : ''}`}>
                <input
                  disabled={loading}
                  className={`pa3 moon-gray ${
                    email.errored ? 'ba b--red' : 'bn'
                  }`}
                  style={{ backgroundColor: '#404041' }}
                  type='text'
                  value={email.value}
                  onFocus={() => handleSetKeyEvent('onFocus', 'email')}
                  onChange={email.onChange}
                />
              </div>
              <div className='red mv2'>{email.message}</div>
              <label className='white mv2'>{labelPassword}</label>
              <div className={`password__wrapper ${keyEvents.focusVisibility?.password ? 'isFocused' : ''}`}>
                <input
                  disabled={loading}
                  className={`pa3 moon-gray w-100 ${
                    password.errored ? 'ba b--red' : 'bn'
                  }`}
                  style={{ backgroundColor: '#404041' }}
                  type={keyEvents.passVisibility ? 'text' : 'password'}
                  value={password.value}
                  onChange={password.onChange}
                  onBlur={() => handleSetKeyEvent('onBlur', 'password')}
                  onFocus={() => handleSetKeyEvent('onFocus', 'password')}
                  onKeyUp={handleCapsLockOnOff}
                  onKeyDown={handleCapsLockOnOff}
                  onKeyPress={handleCapsLockOnOff}
                  onMouseDown={handleCapsLockOnOff}
                />
                <img
                  src={keyEvents.passVisibility ? visibilityOff : visibility}
                  onClick={() => handleSetKeyEvent('onClick', 'visibility')}
                  className='password__toggler'
                  alt='visibility off'
                />
                {isCaps && (
                  <img
                    src={capslock}
                    className='password__capslock-icon'
                    alt='keyboard'
                  />
                )}
              </div>
              <div className='red mv2'>{password.message}</div>
            </>
          )}
        </div>
        {hasValidParams && (
          <>
            <Button
              loading={loading}
              disabled={loading}
              type='submit'
              color={loading ? 'gray' : 'blue'}
              className={`mv3 w-100 white bn login__sign-in-btn ${
                loading ? 'pv2' : 'pv3'
              }`}
            >
              {signIn}
            </Button>
            <span className='login__new-to-roland white mt4'>
              {newToRoland}
              {' '}
              <a className='no-underline blue' href={signUpUrl}>
                {joinNow}
              </a>
            </span>
            <span className='login__forgot-pw white'>
              {forgotPw}
              {' '}
              <a className='no-underline blue' href={forgotUrl}>
                {resetHere}
              </a>
            </span>
          </>
        )}
      </div>
    </form>
  );
};
